import React from "react";

interface IconProps {
  className?: string;
}

/**
 * Vercel-style icon representing unified API access
 * Shows a central hub with multiple connections
 */
export function FeatureUnifiedIcon({ className = "" }: IconProps) {
  return (
    <svg
      viewBox="0 0 200 100"
      className={`h-full w-full ${className}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="xMidYMid meet"
    >
      {/* Background with subtle gradient circles */}
      <circle
        cx="100"
        cy="50"
        r="35"
        stroke="url(#unifiedGrad1)"
        strokeWidth="1"
        opacity="0.6"
      />
      <circle
        cx="100"
        cy="50"
        r="25"
        stroke="url(#unifiedGrad2)"
        strokeWidth="1"
        opacity="0.8"
      />

      {/* Central hub - main API node */}
      <rect
        x="85"
        y="35"
        width="30"
        height="30"
        rx="6"
        fill="white"
        stroke="#171717"
        strokeWidth="1.5"
      />

      {/* Central API indicator */}
      <circle cx="100" cy="50" r="6" fill="#0067D6" />

      {/* Connection paths to peripheral nodes */}
      {/* Top connection */}
      <path
        d="M100 35 L100 15"
        stroke="#0067D6"
        strokeWidth="2"
        strokeLinecap="round"
      />
      {/* Bottom connection */}
      <path
        d="M100 65 L100 85"
        stroke="#CA2A30"
        strokeWidth="2"
        strokeLinecap="round"
      />
      {/* Left connection */}
      <path
        d="M85 50 L45 50"
        stroke="#FFB224"
        strokeWidth="2"
        strokeLinecap="round"
      />
      {/* Right connection */}
      <path
        d="M115 50 L155 50"
        stroke="#067A6F"
        strokeWidth="2"
        strokeLinecap="round"
      />

      {/* Peripheral nodes - rounded rectangles */}
      {/* Top node */}
      <rect
        x="87"
        y="0"
        width="26"
        height="15"
        rx="4"
        fill="white"
        stroke="#525252"
        strokeWidth="1.5"
      />
      <circle cx="100" cy="7.5" r="3" fill="#0067D6" opacity="0.6" />

      {/* Bottom node */}
      <rect
        x="87"
        y="85"
        width="26"
        height="15"
        rx="4"
        fill="white"
        stroke="#525252"
        strokeWidth="1.5"
      />
      <circle cx="100" cy="92.5" r="3" fill="#CA2A30" opacity="0.6" />

      {/* Left node */}
      <rect
        x="10"
        y="42"
        width="30"
        height="16"
        rx="4"
        fill="white"
        stroke="#525252"
        strokeWidth="1.5"
      />
      <circle cx="25" cy="50" r="3" fill="#FFB224" opacity="0.6" />

      {/* Right node */}
      <rect
        x="160"
        y="42"
        width="30"
        height="16"
        rx="4"
        fill="white"
        stroke="#525252"
        strokeWidth="1.5"
      />
      <circle cx="175" cy="50" r="3" fill="#067A6F" opacity="0.6" />

      {/* Small decorative dots on paths */}
      <circle cx="70" cy="50" r="2" fill="#FFB224" />
      <circle cx="130" cy="50" r="2" fill="#067A6F" />
      <circle cx="100" cy="25" r="2" fill="#0067D6" />
      <circle cx="100" cy="75" r="2" fill="#CA2A30" />

      {/* Gradients */}
      <defs>
        <linearGradient
          id="unifiedGrad1"
          x1="135"
          y1="50"
          x2="65"
          y2="50"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#737373" />
          <stop offset="1" stopColor="#FAFAFA" stopOpacity="0" />
        </linearGradient>
        <linearGradient
          id="unifiedGrad2"
          x1="125"
          y1="50"
          x2="75"
          y2="50"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0.5" stopColor="#737373" />
          <stop offset="1" stopColor="#FAFAFA" stopOpacity="0" />
        </linearGradient>
      </defs>
    </svg>
  );
}

/**
 * Vercel-style icon representing high availability and reliability
 * Shows continuous flow with checkmark indicators
 */
export function FeatureAvailabilityIcon({ className = "" }: IconProps) {
  return (
    <svg
      viewBox="0 0 200 100"
      className={`h-full w-full ${className}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="xMidYMid meet"
    >
      {/* Background flow path - curved continuous line */}
      <path
        d="M20 50 Q60 20, 100 50 T180 50"
        stroke="url(#availGrad1)"
        strokeWidth="1"
        fill="none"
        opacity="0.4"
      />

      {/* Main flow path with color segments */}
      <path
        d="M25 50 L65 50"
        stroke="#0067D6"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
      <path
        d="M70 50 L130 50"
        stroke="#067A6F"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
      <path
        d="M135 50 L175 50"
        stroke="#FFB224"
        strokeWidth="2.5"
        strokeLinecap="round"
      />

      {/* Success nodes along the path */}
      {/* First node - start */}
      <circle
        cx="25"
        cy="50"
        r="12"
        fill="white"
        stroke="#0067D6"
        strokeWidth="1.5"
      />
      <path
        d="M20 50 L23 53 L30 46"
        stroke="#0067D6"
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Middle node - processing */}
      <circle
        cx="100"
        cy="50"
        r="14"
        fill="white"
        stroke="#067A6F"
        strokeWidth="1.5"
      />
      <path
        d="M94 50 L98 54 L106 46"
        stroke="#067A6F"
        strokeWidth="2.5"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* End node - complete */}
      <circle
        cx="175"
        cy="50"
        r="12"
        fill="white"
        stroke="#FFB224"
        strokeWidth="1.5"
      />
      <path
        d="M170 50 L173 53 L180 46"
        stroke="#FFB224"
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Decorative elements - small rectangles representing systems */}
      <rect
        x="50"
        y="30"
        width="20"
        height="12"
        rx="3"
        fill="white"
        stroke="#525252"
        strokeWidth="1.5"
        opacity="1"
      />
      <rect
        x="130"
        y="65"
        width="20"
        height="12"
        rx="3"
        fill="white"
        stroke="#525252"
        strokeWidth="1.5"
        opacity="1"
      />

      {/* Pulse indicators */}
      <circle cx="47.5" cy="36" r="2" fill="#0067D6" opacity="0.6">
        <animate
          attributeName="opacity"
          values="0.6;0.2;0.6"
          dur="2s"
          repeatCount="indefinite"
        />
      </circle>
      <circle cx="140" cy="71" r="2" fill="#067A6F" opacity="0.6">
        <animate
          attributeName="opacity"
          values="0.6;0.2;0.6"
          dur="2s"
          begin="1s"
          repeatCount="indefinite"
        />
      </circle>

      {/* Small flow dots between nodes */}
      <circle cx="45" cy="50" r="1.5" fill="#0067D6" />
      <circle cx="55" cy="50" r="1.5" fill="#0067D6" />
      <circle cx="145" cy="50" r="1.5" fill="#FFB224" />
      <circle cx="155" cy="50" r="1.5" fill="#FFB224" />

      {/* Top and bottom accent lines */}
      <line
        x1="40"
        y1="20"
        x2="160"
        y2="20"
        stroke="#737373"
        strokeWidth="1.5"
        strokeDasharray="4 4"
        opacity="0.8"
      />
      <line
        x1="40"
        y1="80"
        x2="160"
        y2="80"
        stroke="#737373"
        strokeWidth="1.5"
        strokeDasharray="4 4"
        opacity="0.8"
      />

      {/* Gradients */}
      <defs>
        <linearGradient
          id="availGrad1"
          x1="20"
          y1="50"
          x2="180"
          y2="50"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#0067D6" stopOpacity="0.3" />
          <stop offset="0.5" stopColor="#067A6F" stopOpacity="0.3" />
          <stop offset="1" stopColor="#FFB224" stopOpacity="0.3" />
        </linearGradient>
      </defs>
    </svg>
  );
}

/**
 * Vercel-style icon representing fair, transparent pricing
 * Shows balanced scales/equality concept
 */
export function FeaturePricingIcon({ className = "" }: IconProps) {
  return (
    <svg
      viewBox="0 0 200 100"
      className={`h-full w-full ${className}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="xMidYMid meet"
    >
      {/* Background decorative circles */}
      <circle
        cx="100"
        cy="50"
        r="40"
        stroke="#525252"
        strokeWidth="1.5"
        opacity="0.8"
      />
      <circle
        cx="100"
        cy="50"
        r="30"
        stroke="#525252"
        strokeWidth="1.5"
        opacity="0.9"
      />

      {/* Central balance point */}
      <circle cx="100" cy="50" r="5" fill="#171717" />

      {/* Horizontal balance beam */}
      <line
        x1="45"
        y1="50"
        x2="155"
        y2="50"
        stroke="#171717"
        strokeWidth="2"
        strokeLinecap="round"
      />

      {/* Left platform/side */}
      <g transform="translate(35, 30)">
        <rect
          x="0"
          y="0"
          width="20"
          height="40"
          rx="4"
          fill="white"
          stroke="#0067D6"
          strokeWidth="1.5"
        />
        {/* Inner content indicator */}
        <rect
          x="5"
          y="8"
          width="10"
          height="6"
          rx="2"
          fill="#0067D6"
          opacity="0.2"
        />
        <rect
          x="5"
          y="18"
          width="10"
          height="6"
          rx="2"
          fill="#0067D6"
          opacity="0.2"
        />
        <rect
          x="5"
          y="28"
          width="10"
          height="6"
          rx="2"
          fill="#0067D6"
          opacity="0.2"
        />
      </g>

      {/* Right platform/side - equal size */}
      <g transform="translate(145, 30)">
        <rect
          x="0"
          y="0"
          width="20"
          height="40"
          rx="4"
          fill="white"
          stroke="#FFB224"
          strokeWidth="1.5"
        />
        {/* Inner content indicator */}
        <rect
          x="5"
          y="8"
          width="10"
          height="6"
          rx="2"
          fill="#FFB224"
          opacity="0.2"
        />
        <rect
          x="5"
          y="18"
          width="10"
          height="6"
          rx="2"
          fill="#FFB224"
          opacity="0.2"
        />
        <rect
          x="5"
          y="28"
          width="10"
          height="6"
          rx="2"
          fill="#FFB224"
          opacity="0.2"
        />
      </g>

      {/* Connection lines from beam to platforms */}
      <line
        x1="45"
        y1="50"
        x2="45"
        y2="30"
        stroke="#737373"
        strokeWidth="1.5"
        strokeDasharray="3 3"
      />
      <line
        x1="155"
        y1="50"
        x2="155"
        y2="30"
        stroke="#737373"
        strokeWidth="1.5"
        strokeDasharray="3 3"
      />

      {/* Central highlight - fairness indicator */}
      <circle
        cx="100"
        cy="50"
        r="8"
        fill="white"
        stroke="#067A6F"
        strokeWidth="2"
      />
      <path
        d="M96 50 L99 53 L104 47"
        stroke="#067A6F"
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Decorative side elements */}
      <circle cx="25" cy="50" r="3" fill="#0067D6" opacity="0.8" />
      <circle cx="175" cy="50" r="3" fill="#FFB224" opacity="0.8" />

      {/* Bottom label area */}
      <rect
        x="75"
        y="82"
        width="50"
        height="12"
        rx="3"
        fill="white"
        stroke="#525252"
        strokeWidth="1.5"
      />
      <circle cx="100" cy="88" r="2.5" fill="#067A6F" />
    </svg>
  );
}

/**
 * Vercel-style icon for Receipt Generation capability
 * Shows a document with signature and checkmark
 */
export function ReceiptGenerationIcon({ className = "" }: IconProps) {
  return (
    <svg
      viewBox="0 0 200 100"
      className={`h-full w-full ${className}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="xMidYMid meet"
    >
      {/* Background frame */}
      <rect
        x="60"
        y="10"
        width="80"
        height="80"
        rx="6"
        fill="white"
        stroke="#525252"
        strokeWidth="1.5"
      />

      {/* Document lines */}
      <line
        x1="75"
        y1="25"
        x2="125"
        y2="25"
        stroke="#737373"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <line
        x1="75"
        y1="35"
        x2="115"
        y2="35"
        stroke="#A3A3A3"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <line
        x1="75"
        y1="45"
        x2="120"
        y2="45"
        stroke="#A3A3A3"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <line
        x1="75"
        y1="55"
        x2="110"
        y2="55"
        stroke="#A3A3A3"
        strokeWidth="1.5"
        strokeLinecap="round"
      />

      {/* Signature line with accent */}
      <line
        x1="75"
        y1="70"
        x2="105"
        y2="70"
        stroke="#0067D6"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <circle cx="108" cy="70" r="3" fill="#0067D6" />

      {/* Digital signature indicator */}
      <path
        d="M90 75 Q95 72, 100 75"
        stroke="#CA2A30"
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
      />

      {/* Success checkmark badge */}
      <circle
        cx="140"
        cy="50"
        r="18"
        fill="white"
        stroke="#067A6F"
        strokeWidth="2"
      />
      <path
        d="M133 50 L137 54 L147 44"
        stroke="#067A6F"
        strokeWidth="3"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Decorative elements */}
      <circle
        cx="30"
        cy="30"
        r="8"
        fill="white"
        stroke="#FFB224"
        strokeWidth="1.5"
        opacity="0.6"
      />
      <rect
        x="155"
        y="65"
        width="20"
        height="20"
        rx="4"
        fill="white"
        stroke="#0067D6"
        strokeWidth="1.5"
        opacity="0.5"
      />
    </svg>
  );
}

/**
 * Vercel-style icon for Signature Verification capability
 * Shows a shield with checkmark and verification elements
 */
export function SignatureVerificationIcon({ className = "" }: IconProps) {
  return (
    <svg
      viewBox="0 0 200 100"
      className={`h-full w-full ${className}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="xMidYMid meet"
    >
      {/* Main shield */}
      <path
        d="M100 15 L130 28 L130 55 Q130 75 100 88 Q70 75 70 55 L70 28 Z"
        fill="white"
        stroke="#525252"
        strokeWidth="2"
      />

      {/* Inner shield border */}
      <path
        d="M100 22 L123 32 L123 55 Q123 70 100 80 Q77 70 77 55 L77 32 Z"
        fill="none"
        stroke="#737373"
        strokeWidth="1.5"
        opacity="0.5"
      />

      {/* Checkmark in center */}
      <circle cx="100" cy="50" r="15" fill="#067A6F" opacity="0.1" />
      <path
        d="M92 50 L97 55 L108 44"
        stroke="#067A6F"
        strokeWidth="3"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Verification indicators around shield */}
      <circle
        cx="35"
        cy="50"
        r="12"
        fill="white"
        stroke="#0067D6"
        strokeWidth="1.5"
      />
      <path
        d="M30 50 L33 53 L40 46"
        stroke="#0067D6"
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      <circle
        cx="165"
        cy="50"
        r="12"
        fill="white"
        stroke="#FFB224"
        strokeWidth="1.5"
      />
      <path
        d="M160 50 L163 53 L170 46"
        stroke="#FFB224"
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Connection lines */}
      <line
        x1="47"
        y1="50"
        x2="70"
        y2="50"
        stroke="#737373"
        strokeWidth="1"
        strokeDasharray="3 3"
      />
      <line
        x1="130"
        y1="50"
        x2="153"
        y2="50"
        stroke="#737373"
        strokeWidth="1"
        strokeDasharray="3 3"
      />

      {/* Top verification badge */}
      <rect x="85" y="0" width="30" height="12" rx="3" fill="#CA2A30" />
      <circle cx="100" cy="6" r="2" fill="white" />
    </svg>
  );
}

/**
 * Vercel-style icon for Webhooks capability
 * Shows connected nodes with pulse signals
 */
export function WebhookPulseIcon({ className = "" }: IconProps) {
  return (
    <svg
      viewBox="0 0 200 100"
      className={`h-full w-full ${className}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="xMidYMid meet"
    >
      {/* Central webhook node */}
      <circle
        cx="100"
        cy="50"
        r="25"
        fill="white"
        stroke="#525252"
        strokeWidth="2"
      />
      <circle
        cx="100"
        cy="50"
        r="15"
        fill="none"
        stroke="#737373"
        strokeWidth="1.5"
      />

      {/* Webhook hook symbol */}
      <path
        d="M100 40 Q108 40, 108 48 Q108 56, 100 56 Q92 56, 92 48"
        stroke="#0067D6"
        strokeWidth="2.5"
        fill="none"
        strokeLinecap="round"
      />
      <circle cx="100" cy="42" r="4" fill="#0067D6" />

      {/* Connected peripheral nodes */}
      {/* Top node */}
      <rect
        x="85"
        y="5"
        width="30"
        height="15"
        rx="4"
        fill="white"
        stroke="#FFB224"
        strokeWidth="1.5"
      />
      <line
        x1="100"
        y1="25"
        x2="100"
        y2="20"
        stroke="#FFB224"
        strokeWidth="2"
      />

      {/* Bottom left node */}
      <rect
        x="20"
        y="70"
        width="30"
        height="15"
        rx="4"
        fill="white"
        stroke="#CA2A30"
        strokeWidth="1.5"
      />
      <line
        x1="50"
        y1="77.5"
        x2="75"
        y2="60"
        stroke="#CA2A30"
        strokeWidth="2"
      />

      {/* Bottom right node */}
      <rect
        x="150"
        y="70"
        width="30"
        height="15"
        rx="4"
        fill="white"
        stroke="#067A6F"
        strokeWidth="1.5"
      />
      <line
        x1="150"
        y1="77.5"
        x2="125"
        y2="60"
        stroke="#067A6F"
        strokeWidth="2"
      />

      {/* Pulse indicators */}
      <circle cx="88" cy="42" r="2" fill="#FFB224">
        <animate
          attributeName="opacity"
          values="1;0.3;1"
          dur="1.5s"
          repeatCount="indefinite"
        />
      </circle>
      <circle cx="55" cy="72" r="2" fill="#CA2A30">
        <animate
          attributeName="opacity"
          values="1;0.3;1"
          dur="1.5s"
          begin="0.5s"
          repeatCount="indefinite"
        />
      </circle>
      <circle cx="155" cy="72" r="2" fill="#067A6F">
        <animate
          attributeName="opacity"
          values="1;0.3;1"
          dur="1.5s"
          begin="1s"
          repeatCount="indefinite"
        />
      </circle>

      {/* Signal waves */}
      <path
        d="M125 50 Q140 50, 145 35"
        stroke="#737373"
        strokeWidth="1"
        fill="none"
        strokeDasharray="2 2"
        opacity="0.6"
      />
      <path
        d="M75 50 Q60 50, 55 65"
        stroke="#737373"
        strokeWidth="1"
        fill="none"
        strokeDasharray="2 2"
        opacity="0.6"
      />

      {/* Small status dots */}
      <circle cx="25" cy="77.5" r="3" fill="#CA2A30" />
      <circle cx="175" cy="77.5" r="3" fill="#067A6F" />
      <circle cx="100" cy="12.5" r="3" fill="#FFB224" />
    </svg>
  );
}

/**
 * Vercel-style icon for Shopify store integration
 * Shows a shopping bag with geometric storefront design
 */
export function FeatureShopifyIcon({ className = "" }: IconProps) {
  return (
    <svg
      viewBox="0 0 200 100"
      className={`h-full w-full ${className}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="xMidYMid meet"
    >
      {/* Background decorative frame */}
      <rect
        x="50"
        y="10"
        width="100"
        height="80"
        rx="8"
        fill="white"
        stroke="#525252"
        strokeWidth="1.5"
      />

      {/* Shopping bag body */}
      <path
        d="M75 35 L75 70 Q75 75 80 75 L120 75 Q125 75 125 70 L125 35"
        fill="white"
        stroke="#171717"
        strokeWidth="2"
      />

      {/* Bag handles - curved */}
      <path
        d="M85 35 Q85 22 92 22 Q99 22 99 35"
        stroke="#0067D6"
        strokeWidth="2.5"
        fill="none"
        strokeLinecap="round"
      />
      <path
        d="M101 35 Q101 22 108 22 Q115 22 115 35"
        stroke="#0067D6"
        strokeWidth="2.5"
        fill="none"
        strokeLinecap="round"
      />

      {/* Storefront window/body elements */}
      <rect
        x="80"
        y="42"
        width="15"
        height="10"
        rx="2"
        fill="#FFB224"
        opacity="0.3"
      />
      <rect
        x="105"
        y="42"
        width="15"
        height="10"
        rx="2"
        fill="#067A6F"
        opacity="0.3"
      />
      <rect
        x="80"
        y="56"
        width="40"
        height="8"
        rx="2"
        fill="#CA2A30"
        opacity="0.2"
      />

      {/* Store awning indicator */}
      <path
        d="M72 35 L128 35"
        stroke="#FFB224"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <circle cx="72" cy="35" r="3" fill="#FFB224" />
      <circle cx="128" cy="35" r="3" fill="#FFB224" />

      {/* Decorative side elements */}
      <circle
        cx="35"
        cy="50"
        r="10"
        fill="white"
        stroke="#0067D6"
        strokeWidth="1.5"
      />
      <text
        x="35"
        y="54"
        textAnchor="middle"
        fill="#0067D6"
        fontSize="10"
        fontWeight="600"
      >
        S
      </text>

      <circle
        cx="165"
        cy="50"
        r="10"
        fill="white"
        stroke="#067A6F"
        strokeWidth="1.5"
      />
      <path
        d="M160 50 L163 53 L170 46"
        stroke="#067A6F"
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Small accent dots */}
      <circle cx="55" cy="25" r="2" fill="#CA2A30" />
      <circle cx="145" cy="75" r="2" fill="#FFB224" />
    </svg>
  );
}

/**
 * Vercel-style icon for Integration capability
 * Shows two platforms connecting via data flow
 */
export function FeatureIntegrationIcon({ className = "" }: IconProps) {
  return (
    <svg
      viewBox="0 0 200 100"
      className={`h-full w-full ${className}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="xMidYMid meet"
    >
      {/* Left platform - Shopify representation */}
      <rect
        x="15"
        y="25"
        width="50"
        height="50"
        rx="6"
        fill="white"
        stroke="#525252"
        strokeWidth="1.5"
      />
      <rect
        x="22"
        y="32"
        width="36"
        height="8"
        rx="2"
        fill="#0067D6"
        opacity="0.2"
      />
      <rect
        x="22"
        y="44"
        width="36"
        height="6"
        rx="2"
        fill="#737373"
        opacity="0.3"
      />
      <rect
        x="22"
        y="54"
        width="36"
        height="6"
        rx="2"
        fill="#737373"
        opacity="0.3"
      />

      {/* Right platform - Receipt representation */}
      <rect
        x="135"
        y="25"
        width="50"
        height="50"
        rx="6"
        fill="white"
        stroke="#525252"
        strokeWidth="1.5"
      />
      <rect
        x="142"
        y="32"
        width="36"
        height="4"
        rx="2"
        fill="#067A6F"
        opacity="0.3"
      />
      <rect
        x="142"
        y="40"
        width="25"
        height="4"
        rx="2"
        fill="#737373"
        opacity="0.3"
      />
      <rect
        x="142"
        y="48"
        width="30"
        height="4"
        rx="2"
        fill="#737373"
        opacity="0.3"
      />
      <rect
        x="142"
        y="56"
        width="20"
        height="4"
        rx="2"
        fill="#737373"
        opacity="0.3"
      />
      <rect
        x="142"
        y="64"
        width="28"
        height="4"
        rx="2"
        fill="#737373"
        opacity="0.3"
      />

      {/* Connection paths - bidirectional */}
      {/* Top path: Shopify -> Receipt */}
      <path
        d="M65 40 Q100 30, 135 40"
        stroke="#0067D6"
        strokeWidth="2.5"
        fill="none"
        strokeLinecap="round"
      />
      <circle cx="100" cy="35" r="3" fill="#0067D6" />
      <path
        d="M130 37 L135 40 L130 43"
        stroke="#0067D6"
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Bottom path: Receipt -> Shopify */}
      <path
        d="M135 60 Q100 70, 65 60"
        stroke="#067A6F"
        strokeWidth="2.5"
        fill="none"
        strokeLinecap="round"
      />
      <circle cx="100" cy="65" r="3" fill="#067A6F" />
      <path
        d="M70 57 L65 60 L70 63"
        stroke="#067A6F"
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Central integration hub */}
      <circle
        cx="100"
        cy="50"
        r="12"
        fill="white"
        stroke="#FFB224"
        strokeWidth="2"
      />
      <circle cx="100" cy="50" r="5" fill="#FFB224" />

      {/* Pulse animations on connections */}
      <circle cx="85" cy="37" r="2" fill="#0067D6" opacity="0.6">
        <animate
          attributeName="opacity"
          values="0.6;0.2;0.6"
          dur="2s"
          repeatCount="indefinite"
        />
      </circle>
      <circle cx="115" cy="63" r="2" fill="#067A6F" opacity="0.6">
        <animate
          attributeName="opacity"
          values="0.6;0.2;0.6"
          dur="2s"
          begin="1s"
          repeatCount="indefinite"
        />
      </circle>
    </svg>
  );
}

/**
 * Vercel-style icon for Automation capability
 * Shows gears with automated workflow
 */
export function FeatureAutomationIcon({ className = "" }: IconProps) {
  return (
    <svg
      viewBox="0 0 200 100"
      className={`h-full w-full ${className}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="xMidYMid meet"
    >
      {/* Main automation gear - center */}
      <g transform="translate(100, 50)">
        {/* Gear teeth */}
        <path
          d="M-20 -25 L-15 -35 L15 -35 L20 -25 L20 -15 L30 -10 L30 10 L20 15 L20 25 L15 35 L-15 35 L-20 25 L-20 15 L-30 10 L-30 -10 L-20 -15 Z"
          fill="white"
          stroke="#171717"
          strokeWidth="1.5"
        />
        {/* Center hole */}
        <circle cx="0" cy="0" r="10" fill="#0067D6" />
        <circle cx="0" cy="0" r="5" fill="white" />
      </g>

      {/* Smaller gear - left */}
      <g transform="translate(45, 50)">
        <path
          d="M-12 -15 L-9 -20 L9 -20 L12 -15 L12 -9 L18 -6 L18 6 L12 9 L12 15 L9 20 L-9 20 L-12 15 L-12 9 L-18 6 L-18 -6 L-12 -9 Z"
          fill="white"
          stroke="#525252"
          strokeWidth="1.5"
        />
        <circle cx="0" cy="0" r="6" fill="#CA2A30" />
      </g>

      {/* Smaller gear - right */}
      <g transform="translate(155, 50)">
        <path
          d="M-12 -15 L-9 -20 L9 -20 L12 -15 L12 -9 L18 -6 L18 6 L12 9 L12 15 L9 20 L-9 20 L-12 15 L-12 9 L-18 6 L-18 -6 L-12 -9 Z"
          fill="white"
          stroke="#525252"
          strokeWidth="1.5"
        />
        <circle cx="0" cy="0" r="6" fill="#067A6F" />
      </g>

      {/* Automation flow arrows */}
      {/* Top flow */}
      <path
        d="M65 35 Q82 25, 100 30"
        stroke="#FFB224"
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
        strokeDasharray="4 2"
      />
      <path
        d="M95 28 L100 30 L97 35"
        stroke="#FFB224"
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Bottom flow */}
      <path
        d="M100 70 Q118 75, 135 65"
        stroke="#FFB224"
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
        strokeDasharray="4 2"
      />
      <path
        d="M130 63 L135 65 L132 70"
        stroke="#FFB224"
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Connection lines between gears */}
      <line
        x1="57"
        y1="50"
        x2="80"
        y2="50"
        stroke="#737373"
        strokeWidth="1"
        strokeDasharray="3 3"
      />
      <line
        x1="120"
        y1="50"
        x2="143"
        y2="50"
        stroke="#737373"
        strokeWidth="1"
        strokeDasharray="3 3"
      />

      {/* Status indicators */}
      <circle cx="25" cy="30" r="4" fill="#0067D6" opacity="0.5" />
      <circle cx="175" cy="70" r="4" fill="#067A6F" opacity="0.5" />
      <circle cx="100" y="10" r="3" fill="#FFB224" />
    </svg>
  );
}

/**
 * Vercel-style icon for Sync capability
 * Shows circular arrows representing continuous synchronization
 */
export function FeatureSyncIcon({ className = "" }: IconProps) {
  return (
    <svg
      viewBox="0 0 200 100"
      className={`h-full w-full ${className}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="xMidYMid meet"
    >
      {/* Central sync orbit circle */}
      <circle
        cx="100"
        cy="50"
        r="35"
        fill="none"
        stroke="#525252"
        strokeWidth="1.5"
        strokeDasharray="5 3"
        opacity="0.6"
      />

      {/* Left sync node - Shopify */}
      <rect
        x="25"
        y="35"
        width="40"
        height="30"
        rx="5"
        fill="white"
        stroke="#0067D6"
        strokeWidth="2"
      />
      <rect
        x="32"
        y="42"
        width="26"
        height="6"
        rx="2"
        fill="#0067D6"
        opacity="0.2"
      />
      <rect
        x="32"
        y="52"
        width="18"
        height="4"
        rx="2"
        fill="#737373"
        opacity="0.3"
      />

      {/* Right sync node - Receipt */}
      <rect
        x="135"
        y="35"
        width="40"
        height="30"
        rx="5"
        fill="white"
        stroke="#067A6F"
        strokeWidth="2"
      />
      <rect
        x="142"
        y="42"
        width="26"
        height="4"
        rx="2"
        fill="#067A6F"
        opacity="0.2"
      />
      <rect
        x="142"
        y="50"
        width="20"
        height="4"
        rx="2"
        fill="#737373"
        opacity="0.2"
      />
      <rect
        x="142"
        y="58"
        width="14"
        height="4"
        rx="2"
        fill="#737373"
        opacity="0.2"
      />

      {/* Top sync arrow - clockwise */}
      <path
        d="M70 35 Q100 15, 130 35"
        stroke="#FFB224"
        strokeWidth="2.5"
        fill="none"
        strokeLinecap="round"
      />
      <path
        d="M125 32 L130 35 L127 40"
        stroke="#FFB224"
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Bottom sync arrow - counter-clockwise */}
      <path
        d="M130 65 Q100 85, 70 65"
        stroke="#CA2A30"
        strokeWidth="2.5"
        fill="none"
        strokeLinecap="round"
      />
      <path
        d="M75 68 L70 65 L73 60"
        stroke="#CA2A30"
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Central sync indicator */}
      <circle
        cx="100"
        cy="50"
        r="10"
        fill="white"
        stroke="#171717"
        strokeWidth="2"
      />
      <path
        d="M95 50 L98 53 L105 47"
        stroke="#067A6F"
        strokeWidth="2.5"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Decorative pulse dots on arrows */}
      <circle cx="100" cy="25" r="2.5" fill="#FFB224">
        <animate
          attributeName="opacity"
          values="1;0.3;1"
          dur="2s"
          repeatCount="indefinite"
        />
      </circle>
      <circle cx="100" cy="75" r="2.5" fill="#CA2A30">
        <animate
          attributeName="opacity"
          values="1;0.3;1"
          dur="2s"
          begin="1s"
          repeatCount="indefinite"
        />
      </circle>

      {/* Small accent elements */}
      <circle cx="15" cy="50" r="3" fill="#0067D6" />
      <circle cx="185" cy="50" r="3" fill="#067A6F" />
    </svg>
  );
}

/**
 * Step 1 icon: One-click connection
 * Shows a cursor clicking on a button
 */
export function StepOneClickIcon({ className = "" }: IconProps) {
  return (
    <svg
      viewBox="0 0 200 100"
      className={`h-full w-full ${className}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="xMidYMid meet"
    >
      {/* Main button being clicked */}
      <rect
        x="70"
        y="35"
        width="60"
        height="30"
        rx="6"
        fill="white"
        stroke="#171717"
        strokeWidth="2"
      />
      <rect
        x="75"
        y="42"
        width="50"
        height="4"
        rx="2"
        fill="#0067D6"
        opacity="0.3"
      />
      <rect
        x="85"
        y="50"
        width="30"
        height="4"
        rx="2"
        fill="#737373"
        opacity="0.3"
      />

      {/* Cursor pointer */}
      <path
        d="M130 45 L140 55 L135 55 L138 62 L132 64 L129 57 L124 61 Z"
        fill="#FFB224"
        stroke="#FFB224"
        strokeWidth="1"
      />

      {/* Click ripple effect */}
      <circle
        cx="100"
        cy="50"
        r="20"
        stroke="#FFB224"
        strokeWidth="1"
        opacity="0.3"
        strokeDasharray="3 3"
      />
      <circle
        cx="100"
        cy="50"
        r="28"
        stroke="#FFB224"
        strokeWidth="1"
        opacity="0.2"
        strokeDasharray="4 4"
      />

      {/* Decorative elements */}
      <circle
        cx="35"
        cy="35"
        r="6"
        fill="white"
        stroke="#0067D6"
        strokeWidth="1.5"
      />
      <text
        x="35"
        y="38"
        textAnchor="middle"
        fill="#0067D6"
        fontSize="8"
        fontWeight="700"
      >
        1
      </text>

      <circle
        cx="165"
        cy="65"
        r="6"
        fill="white"
        stroke="#067A6F"
        strokeWidth="1.5"
      />
      <path
        d="M162 65 L165 68 L168 62"
        stroke="#067A6F"
        strokeWidth="1.5"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Spark lines */}
      <line
        x1="135"
        y1="30"
        x2="140"
        y2="25"
        stroke="#FFB224"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <line
        x1="145"
        y1="35"
        x2="152"
        y2="32"
        stroke="#FFB224"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

/**
 * Step 2 icon: Full integration
 * Shows connected puzzle pieces
 */
export function StepIntegrationIcon({ className = "" }: IconProps) {
  return (
    <svg
      viewBox="0 0 200 100"
      className={`h-full w-full ${className}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="xMidYMid meet"
    >
      {/* Left puzzle piece - Shopify */}
      <path
        d="M40 30 L70 30 L70 40 Q75 40 75 45 Q75 50 70 50 L70 70 L40 70 L40 60 Q35 60 35 55 Q35 50 40 50 L40 30 Z"
        fill="white"
        stroke="#0067D6"
        strokeWidth="2"
      />
      <rect
        x="48"
        y="40"
        width="14"
        height="4"
        rx="2"
        fill="#0067D6"
        opacity="0.3"
      />
      <rect
        x="48"
        y="50"
        width="10"
        height="4"
        rx="2"
        fill="#737373"
        opacity="0.3"
      />

      {/* Right puzzle piece - Receipt */}
      <path
        d="M130 30 L160 30 L160 50 Q165 50 165 55 Q165 60 160 60 L160 70 L130 70 L130 60 Q125 60 125 55 Q125 50 130 50 L130 30 Z"
        fill="white"
        stroke="#067A6F"
        strokeWidth="2"
      />
      <rect
        x="138"
        y="38"
        width="14"
        height="3"
        rx="1.5"
        fill="#067A6F"
        opacity="0.3"
      />
      <rect
        x="138"
        y="45"
        width="10"
        height="3"
        rx="1.5"
        fill="#737373"
        opacity="0.3"
      />
      <rect
        x="138"
        y="52"
        width="12"
        height="3"
        rx="1.5"
        fill="#737373"
        opacity="0.3"
      />

      {/* Connection bridge */}
      <rect
        x="75"
        y="45"
        width="50"
        height="10"
        rx="5"
        fill="#FFB224"
        opacity="0.2"
      />
      <circle cx="100" cy="50" r="8" fill="#FFB224" />
      <path
        d="M96 50 L99 53 L104 47"
        stroke="white"
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Step number */}
      <circle
        cx="30"
        cy="75"
        r="6"
        fill="white"
        stroke="#0067D6"
        strokeWidth="1.5"
      />
      <text
        x="30"
        y="78"
        textAnchor="middle"
        fill="#0067D6"
        fontSize="8"
        fontWeight="700"
      >
        2
      </text>

      {/* Checkmark */}
      <circle
        cx="170"
        cy="25"
        r="6"
        fill="white"
        stroke="#067A6F"
        strokeWidth="1.5"
      />
      <path
        d="M167 25 L169 27 L173 23"
        stroke="#067A6F"
        strokeWidth="1.5"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Step 3 icon: Full automation
 * Shows a robot/gear with automated flow
 */
export function StepAutomationIcon({ className = "" }: IconProps) {
  return (
    <svg
      viewBox="0 0 200 100"
      className={`h-full w-full ${className}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="xMidYMid meet"
    >
      {/* Central automation hub */}
      <circle
        cx="100"
        cy="50"
        r="25"
        fill="white"
        stroke="#171717"
        strokeWidth="2"
      />

      {/* Gear teeth */}
      <rect x="95" y="18" width="10" height="8" rx="2" fill="#FFB224" />
      <rect x="95" y="74" width="10" height="8" rx="2" fill="#FFB224" />
      <rect x="68" y="45" width="8" height="10" rx="2" fill="#FFB224" />
      <rect x="124" y="45" width="8" height="10" rx="2" fill="#FFB224" />

      {/* Inner gear */}
      <circle
        cx="100"
        cy="50"
        r="15"
        fill="none"
        stroke="#FFB224"
        strokeWidth="2"
        strokeDasharray="4 2"
      />
      <circle cx="100" cy="50" r="8" fill="#FFB224" />
      <circle cx="100" cy="50" r="3" fill="white" />

      {/* Input flow - left */}
      <rect
        x="25"
        y="38"
        width="30"
        height="24"
        rx="4"
        fill="white"
        stroke="#0067D6"
        strokeWidth="1.5"
      />
      <rect
        x="30"
        y="44"
        width="20"
        height="4"
        rx="2"
        fill="#0067D6"
        opacity="0.3"
      />
      <rect
        x="30"
        y="52"
        width="12"
        height="4"
        rx="2"
        fill="#737373"
        opacity="0.3"
      />

      {/* Output flow - right */}
      <rect
        x="145"
        y="38"
        width="30"
        height="24"
        rx="4"
        fill="white"
        stroke="#067A6F"
        strokeWidth="1.5"
      />
      <rect
        x="150"
        y="44"
        width="20"
        height="4"
        rx="2"
        fill="#067A6F"
        opacity="0.3"
      />
      <rect
        x="150"
        y="52"
        width="16"
        height="4"
        rx="2"
        fill="#737373"
        opacity="0.3"
      />

      {/* Flow arrows */}
      <path
        d="M55 50 L75 50"
        stroke="#0067D6"
        strokeWidth="2"
        strokeLinecap="round"
        strokeDasharray="3 3"
      />
      <path
        d="M72 47 L75 50 L72 53"
        stroke="#0067D6"
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      <path
        d="M125 50 L145 50"
        stroke="#067A6F"
        strokeWidth="2"
        strokeLinecap="round"
        strokeDasharray="3 3"
      />
      <path
        d="M142 47 L145 50 L142 53"
        stroke="#067A6F"
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Step number */}
      <circle
        cx="35"
        cy="75"
        r="6"
        fill="white"
        stroke="#0067D6"
        strokeWidth="1.5"
      />
      <text
        x="35"
        y="78"
        textAnchor="middle"
        fill="#0067D6"
        fontSize="8"
        fontWeight="700"
      >
        3
      </text>

      {/* Automation indicator */}
      <circle
        cx="165"
        cy="25"
        r="6"
        fill="white"
        stroke="#CA2A30"
        strokeWidth="1.5"
      />
      <circle cx="165" cy="25" r="2" fill="#CA2A30" />
    </svg>
  );
}
